import React, { useState, useRef, DragEvent, ChangeEvent } from 'react';
import './customUpload.css'
import { Icon } from '../globalIcons';

interface CustomUploadProps {
  onFileSelect: (file: File | null) => void;
  accept?: string;
  maxSize?: number; // in MB
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: string;
  isClosable?:boolean;
  handlCloseBtnClick?:()=>void;
}

const CustomUpload: React.FC<CustomUploadProps> = ({
  onFileSelect,
  accept = '*',
  maxSize = 10,
  placeholder = 'Drop files here or click to browse',
  disabled = false,
  className = '',
  error,
  isClosable=false,
  handlCloseBtnClick
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): boolean => {
    if (file.size > maxSize * 1024 * 1024) {
      setUploadError(`File size must be less than ${maxSize}MB`);
      return false;
    }
    setUploadError('');
    return true;
  };

  const handleFile = (file: File) => {
    if (validateFile(file)) {
      setSelectedFile(file);
      onFileSelect(file);
    } else {
      onFileSelect(null);
    }
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedFile(null);
    onFileSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const displayError = error || uploadError;

  const getUploadZoneClass = () => {
    let baseClass = 'upload-zone';
    if (isDragActive) baseClass += ' upload-zone--drag-active';
    if (selectedFile) baseClass += ' upload-zone--has-file';
    if (displayError) baseClass += ' upload-zone--error';
    if (disabled) baseClass += ' upload-zone--disabled';
    return baseClass;
  };

  return (
    <>
      <div className={`custom-upload ${className}`}>
        {
          isClosable  && !selectedFile && <div className='custom__modified__upload-close-btn'>
            <Icon iconClassName='modified__custom__upload__close__btn' onIconClick={handlCloseBtnClick}/>  
          </div>
        }
        <div
          className={getUploadZoneClass()}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type='file'
            accept={accept}
            onChange={handleFileInput}
            className='file-input'
            disabled={disabled}
          />

          {selectedFile ? (
              <div className='file-display'>
                <div className='file-info'>
                  <div className='file-icon'>
                    <svg className='icon' viewBox='0 0 24 24' fill='currentColor'>
                      <path d='M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z' />
                    </svg>
                  </div>
                  <div className='file-details'>
                    <p className='file-name'>{selectedFile.name}</p>
                    <p className='file-size'>{formatFileSize(selectedFile.size)}</p>
                  </div>
                </div>
                <div className='file-actions'>
                  <svg className='success-icon' viewBox='0 0 24 24' fill='currentColor'>
                    <path d='M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z' />
                  </svg>
                  <button
                    type='button'
                    onClick={(e)=>{
                      handleRemoveFile(e);
                      if(isClosable && handlCloseBtnClick){
                        handlCloseBtnClick()
                      }
                    }}
                    className='remove-button'
                  >
                    <svg className='icon' viewBox='0 0 24 24' fill='currentColor'>
                      <path d='M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z' />
                    </svg>
                  </button>
                </div>
              </div>
          ) : (
            <div className='upload-prompt'>
              <div className='upload-icon-container'>
                <div className='upload-icon-wrapper'>
                  <svg className={`upload-icon ${isDragActive ? 'active' : ''}`} viewBox='0 0 24 24' fill='currentColor'>
                    <path d='M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M17,13V15.5L16.5,16H15V14.5L15.5,14H17M9.5,14H11V16H14V18H9V14.5L9.5,14Z' />
                  </svg>
                </div>
              </div>
              <div className='upload-text'>
                <p className={`upload-message ${isDragActive ? 'active' : ''}`}>
                  {isDragActive ? 'Drop your file here' : placeholder}
                </p>
                <p className='upload-hint'>
                  Maximum file size: {maxSize}MB
                </p>
              </div>
            </div>
          )}

          {isDragActive && <div className='drag-overlay' />}
        </div>

        {displayError && (
          <p className='error-message'>{displayError}</p>
        )}
      </div>
    </>
  );
};

export default CustomUpload;